/**
 * Small supplies surcharge (#2465, split out of the 991-line workshop form).
 *
 * Owns its own text inputs because percentage and fixed amount are held as
 * strings while typing and only committed to `settings` on blur — the same
 * comma/period handling the rest of the money fields use.
 */

import { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings, SmallSuppliesSurcharge, SurchargeType } from '@/types';
import { DEFAULT_VAT_PERCENT } from '@/utils/vat';
import { intlLocale } from '@/utils/locale';
import { eurosToCents } from '@/utils/money';

/**
 * The tenant's default VAT rate, resolved the way the invoice does it: the
 * rate named by `defaultVatRateId`, else the statutory default.
 */
function defaultVatPercent(settings: WorkshopSettings): number {
  const rate = (settings.vatRates ?? []).find((entry) => entry.id === settings.defaultVatRateId);
  return rate?.percentage ?? DEFAULT_VAT_PERCENT;
}

interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
}

export function WorkshopSectionSurcharge({ settings, onChange }: Props) {
  const { t } = useTranslation('settings');
  // The surcharge goes on the invoice as a net line and VAT is added on top
  // at the default rate (#4119), so say so next to the amount.
  const vatRateLabel = new Intl.NumberFormat(intlLocale(), { style: 'percent', maximumFractionDigits: 2 })
    .format(defaultVatPercent(settings) / 100);

  // Small supplies surcharge state
  // Small supplies surcharge state — null means disabled
  const surcharge = settings.smallSuppliesSurcharge;
  const surchargeEnabled = surcharge != null;

  const [surchargePercentInput, setSurchargePercentInput] = useState(
    String(surcharge?.percentage ?? 0).replace('.', ','),
  );
  const [surchargeFixedInput, setSurchargeFixedInput] = useState(
    surcharge?.fixedCents != null ? ((surcharge.fixedCents) / 100).toFixed(2).replace('.', ',') : '',
  );

  const handleSurchargeChange = (patch: Partial<SmallSuppliesSurcharge>) => {
    onChange({
      ...settings,
      smallSuppliesSurcharge: { type: 'labor', ...surcharge, ...patch },
    });
  };

  const handleSurchargeToggle = (enabled: boolean) => {
    onChange({
      ...settings,
      smallSuppliesSurcharge: enabled ? { type: 'labor', percentage: 0 } : undefined,
    });
  };

  return (
    <>
      {/* Small Supplies Surcharge */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('smallSuppliesSurcharge.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('smallSuppliesSurcharge.subtitle')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <FormControlLabel
          control={
            <Switch
              checked={surchargeEnabled}
              onChange={(e) => handleSurchargeToggle(e.target.checked)}
            />
          }
          label={t('smallSuppliesSurcharge.enabled')}
          sx={{ mb: 2 }}
        />

        {surchargeEnabled && surcharge && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label={t('smallSuppliesSurcharge.basis')}
                value={surcharge.type}
                onChange={(e) => handleSurchargeChange({ type: e.target.value as SurchargeType })}
              >
                <MenuItem value="labor">{t('smallSuppliesSurcharge.basisLabor')}</MenuItem>
                <MenuItem value="parts">{t('smallSuppliesSurcharge.basisParts')}</MenuItem>
                <MenuItem value="total">{t('smallSuppliesSurcharge.basisTotal')}</MenuItem>
                <MenuItem value="fixed">{t('smallSuppliesSurcharge.basisFixed')}</MenuItem>
              </TextField>
            </Grid>

            {surcharge.type !== 'fixed' ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('smallSuppliesSurcharge.percent')}
                  value={surchargePercentInput}
                  onChange={(e) => setSurchargePercentInput(e.target.value)}
                  onBlur={() => {
                    const val = parseFloat(surchargePercentInput.replace(',', '.'));
                    const percentage = isNaN(val) ? 0 : Math.max(0, Math.min(100, val));
                    handleSurchargeChange({ percentage });
                    setSurchargePercentInput(String(percentage).replace('.', ','));
                  }}
                  helperText={`${t('smallSuppliesSurcharge.percentHint')} · ${t('smallSuppliesSurcharge.vatNote', { rate: vatRateLabel })}`}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    },
                  }}
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('smallSuppliesSurcharge.fixedAmount')}
                  value={surchargeFixedInput}
                  onChange={(e) => setSurchargeFixedInput(e.target.value)}
                  onBlur={() => {
                    // eurosToCents, not Math.round(euros * 100): 1,005 → 101 (docs/NUMERIC-PRECISION.md)
                    const cents = Math.max(0, eurosToCents(surchargeFixedInput) ?? 0);
                    handleSurchargeChange({ fixedCents: cents });
                    setSurchargeFixedInput((cents / 100).toFixed(2).replace('.', ','));
                  }}
                  helperText={`${t('smallSuppliesSurcharge.fixedAmountHint')} · ${t('smallSuppliesSurcharge.vatNote', { rate: vatRateLabel })}`}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </>
  );
}
